import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import {
  getByUidMutation,
  getAllQuery,
  buildSiteMutation,
  serializeData,
  updateByIdMutation,
  createOneMutation,
  deleteByIdMutation,
} from '@apis/mediacoreQueries'
import { DNSRecord, Group, Module, ModuleGroupRelation, Permission, Role, tokkoResponse, User, Website } from '@types'
import { RootState } from '@redux/store'
import { userSlice } from '@slices/userSlice'
import { auth } from '@firebaseUtils/firebase'

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.GATSBY_API_BASE_URL,
  prepareHeaders: async (headers: Headers, { getState }) => {
    const idToken = (getState() as RootState).user.idToken
    if (idToken) {
      headers.set('Authorization', `JWT ${idToken}`)
    }
    return headers
  },
})

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  let result = await baseQuery(args, api, extraOptions)
  if (result.error?.status === 403 && (result.error.data as { detail: string })?.detail?.includes('Token expired')) {
    const idToken = await auth.currentUser?.getIdToken(true)
    if (idToken) {
      api.dispatch(userSlice.actions.updateIdToken(idToken))
      result = await baseQuery(args, api, extraOptions)
    }
  }
  return result
}

// Define a service using a base URL and expected endpoints
export const mediacoreApi = createApi({
  reducerPath: 'mediacoreApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'DNSRecord',
    'Notification',
    'UserConfig',
    'Analytics',
    'Website',
    'Site',
    'User',
    'Role',
    'Client',
    'Module',
    'Permission',
    'Action',
    'Template',
    'TemplateCategory',
    'Gallery',
    'Font',
    'News',
    'Tags',
    'Development',
    'Profile',
    'Investor',
    'GeoCodeCity',
    'VisitRecord',
    'Appraisal',
    'TextTemplate',
    'Report',
    'LoginTemplate',
    'TokkoProperty',
    'Endpoint',
    'Group',
    'ModuleGroupRelation',
    'Owner',
    'TokkoDevelopment',
    'Property',
    'SiteDevelopment',
    'PropertiesGroup',
    'DevelopmentsGrouping',
    'Position',
    'Application',
    'Contact',
    'SiteComponent',
    'SiteV2',
    'SiteV2GetData',
    'SiteV2Deploy',
    'SiteCompiler',
    'SiteTemplate',
    'SupportTicket',
    'NotificationSetting',
    'CommunicationTable',
    'UserCommunicationTable',
    'VisitRecordTable',
    'InvestorNewsTable',
    'InvestorNews',
    'BlogNewsTable',
    'OwnerTable',
    'InvestorTable',
    'ApplicationTable',
    'PositionTable',
    'TicketTable',
    'ClientTable',
    'UserTable',
    'ActionTable',
    'PageTable',
    'ContactTable',
    'PropertyTable',
    'PropertyMixTable',
    'TokkoPropertyTable',
    'ContactEvent',
    'NotificationsCount',
    'ProductTable',
    'OwnerNews',
    'Resource',
    'ResourceEvent',
    'ResourceTable',
    'TermsAndConditions',
    'ControlTicket',
    'ControlTicketTable',
    'TicketEvent',
    'ClientEvent',
    'NotificationTable',
    'MarketingTask',
    'MarketingTaskEvent',
    'MarketingTaskTable',
    'MarketingObjective',
    'MarketingObjectiveAction',
    'Guide',
    'GuideTable',
  ],
  endpoints: (builder) => ({
    getByUid: builder.mutation<User, { uid: string }>({ query: getByUidMutation }),
    buildSite: builder.mutation<{ success: boolean }, { siteId: number; clientId: number }>({
      query: buildSiteMutation,
    }),
    buildSiteV2: builder.mutation<{ success: boolean }, { siteId: number; clientId: number }>({
      query: ({ siteId, clientId: client }: { siteId: number; clientId: number }) => ({
        url: `site/sitios-v2/buildsite/${siteId}/`,
        method: 'POST',
        body: { client },
      }),
      invalidatesTags: ['SiteV2Deploy'],
    }),
    buildSiteCompilerV2: builder.mutation<{ success: boolean }, { siteId: number; clientId: number }>({
      query: ({ siteId, clientId: client }: { siteId: number; clientId: number }) => ({
        url: `site/sitios-v2/buildsitecompiler/${siteId}/`,
        method: 'POST',
        body: { client },
      }),
      invalidatesTags: ['SiteV2Deploy'],
    }),
    getSiteV2GetData: builder.query<any, { siteId: number }>({
      query: ({ siteId }: { siteId: number }) => ({
        url: `site/sitios-v2/${siteId}/get_data/`,
        method: 'GET',
      }),
      providesTags: ['SiteV2GetData'],
    }),
    getSiteV2Deploys: builder.query<any, { siteId: number; isSiteCompiler?: boolean }>({
      query: ({ siteId, isSiteCompiler }: { siteId: number; isSiteCompiler?: boolean }) => ({
        url: `site/sitios-v2/${siteId}/get_deploys/`,
        method: 'GET',
        params: Object.assign({ client: 0 }, isSiteCompiler ? { is_site_compiler: 'true' } : {}),
      }),
      providesTags: ['SiteV2Deploy'],
    }),
    getRoles: builder.query<Role[], void>({
      query: getAllQuery('core/roles'),
      providesTags: ['Role'],
    }),

    getModules: builder.query<Module[], void>({
      query: getAllQuery('core/modulos'),
      providesTags: ['Module'],
    }),
    getPermissions: builder.query<Permission[], void>({
      query: getAllQuery('core/permisos'),
      providesTags: ['Permission'],
    }),
    updatePermission: builder.mutation<Permission, { id: number; data: Partial<Permission> }>({
      query: updateByIdMutation('core/permisos'),
      invalidatesTags: ['Permission'],
    }),
    createPermission: builder.mutation<Permission, { data: Partial<Permission> }>({
      query: createOneMutation('core/permisos'),
      invalidatesTags: ['Permission'],
    }),
    deletePermission: builder.mutation<void, { id: number }>({
      query: deleteByIdMutation('core/permisos'),
      invalidatesTags: ['Permission'],
    }),
    getModuleGroupRelations: builder.query<ModuleGroupRelation[], void>({
      query: getAllQuery('core/permisos-modulos'),
      providesTags: ['ModuleGroupRelation'],
    }),
    createModuleGroupRelation: builder.mutation<ModuleGroupRelation, { data: Partial<ModuleGroupRelation> }>({
      query: createOneMutation('core/permisos-modulos'),
      invalidatesTags: ['ModuleGroupRelation'],
    }),
    updateModuleGroupRelation: builder.mutation<
      ModuleGroupRelation,
      { id: number; data: Partial<ModuleGroupRelation> }
    >({
      query: updateByIdMutation('core/permisos-modulos'),
      invalidatesTags: ['ModuleGroupRelation'],
    }),
    getGroups: builder.query<Group[], void>({
      query: getAllQuery('core/permisos-grupos'),
      providesTags: ['Group', 'ModuleGroupRelation'],
    }),
    createGroup: builder.mutation<Group, { data: Partial<Group> }>({
      query: createOneMutation('core/permisos-grupos'),
      invalidatesTags: ['Group', 'ModuleGroupRelation'],
    }),
    updateGroup: builder.mutation<Group, { id: number; data: Partial<Group> }>({
      query: updateByIdMutation('core/permisos-grupos'),
      invalidatesTags: ['Group', 'ModuleGroupRelation'],
    }),
    deleteGroup: builder.mutation<void, { id: number }>({
      query: deleteByIdMutation('core/permisos-grupos'),
      invalidatesTags: ['Group', 'ModuleGroupRelation'],
    }),
    getWebsites: builder.query<Website[], void>({
      query: getAllQuery('site/websites'),
      providesTags: ['Website'],
    }),
    getDNSRecords: builder.query<DNSRecord[], void>({
      query: getAllQuery('site/dns'),
      providesTags: ['DNSRecord'],
    }),
    getGeoCodeCities: builder.query<any, { cities: string[] }>({
      query: ({ cities }: { cities: string[] }) => ({
        url: 'core/geocode-cities/',
        method: 'POST',
        body: {
          cities,
        },
      }),
    }),
    uploadFile: builder.mutation<any, { data: any }>({
      query: ({ data }: { data: any }) => ({
        url: 'core/usuarios/avatar/',
        method: 'POST',
        body: serializeData(data),
      }),
    }),
    getTokkoDevelopmentsCount: builder.query<{ total_count: number }, { client: number }>({
      query: ({ client }: { client: number }) => ({
        url: 'owner/externo/tokko-desarrollos/total_count/',
        params: {
          client,
        },
      }),
    }),
    getTokkoPropertiesCount: builder.query<tokkoResponse, { client: number }>({
      query: ({ client }: { client: number }) => ({
        url: 'owner/externo/tokko-propiedades/get_search_summary/',
        params: {
          client,
        },
      }),
    }),
    getEndpoints: builder.query<string[], void>({
      query: getAllQuery('core/endpoints'),
      providesTags: ['Endpoint'],
    }),
    getHasPermission: builder.query<
      { detail: string },
      { client: number; module: string; method: string; endpoint: string }
    >({
      query: ({
        client,
        module,
        method,
        endpoint,
      }: {
        client: number
        module: string
        method: string
        endpoint: string
      }) => ({
        url: 'core/permisos/has_permission/',
        params: {
          client,
          module,
          method,
          endpoint,
        },
      }),
    }),
    getHasSomePermission: builder.query<{ available_modules: string[] }, { client: number }>({
      query: ({ client }: { client: number }) => ({
        url: 'core/permisos/has_some_permission/',
        params: { client },
      }),
    }),
    getIsInvestor: builder.query<{ detail: string }, { client: number }>({
      query: ({ client }: { client: number }) => ({
        url: 'core/permisos/is_investor/',
        params: {
          client,
        },
      }),
    }),
    getIsOwner: builder.query<{ detail: string }, { client: number }>({
      query: ({ client }: { client: number }) => ({
        url: 'core/permisos/is_owner/',
        params: {
          client,
        },
      }),
    }),
    getTokkoDevelopments: builder.query<any[], { client: number; search?: string; ids?: number[] }>({
      query: ({ client, search, ids }: { client: number; search?: string; ids?: number[] }) => ({
        url: 'owner/externo/tokko-desarrollos/',
        params: Object.assign(
          {
            client,
          },
          ids ? { ids } : {},
          search ? { search } : {},
        ),
      }),
      providesTags: ['TokkoDevelopment'],
    }),
    getComponentUsage: builder.query<
      Array<{
        client_id: number
        client_name: string
        site_id: number
        page_id: number
        page_path: string
        page_title: string
      }>,
      { id: number; clientId: number; type?: string }
    >({
      query: ({ id, clientId, type }) => ({
        url: `site/sitios-componentes/${id}/usage/`,
        method: 'GET',
        params: { client: clientId, ...(type ? { type } : {}) },
      }),
      providesTags: (_result, _error, { id }) => [{ type: 'SiteComponent', id }, 'PageTable'],
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetByUidMutation,
  useGetRolesQuery,
  usePrefetch,
  useGetModulesQuery,
  useGetPermissionsQuery,
  useGetGroupsQuery,
  useGetModuleGroupRelationsQuery,
  useBuildSiteMutation,
  useGetWebsitesQuery,
  useGetDNSRecordsQuery,
  useGetGeoCodeCitiesQuery,
  useUploadFileMutation,
  useGetTokkoDevelopmentsCountQuery,
  useGetTokkoPropertiesCountQuery,
  useUpdatePermissionMutation,
  useCreatePermissionMutation,
  useDeletePermissionMutation,
  useCreateGroupMutation,
  useUpdateModuleGroupRelationMutation,
  useDeleteGroupMutation,
  useUpdateGroupMutation,
  useCreateModuleGroupRelationMutation,
  useGetEndpointsQuery,
  useGetHasPermissionQuery,
  useGetHasSomePermissionQuery,
  useGetIsInvestorQuery,
  useGetIsOwnerQuery,
  useGetTokkoDevelopmentsQuery,
  useBuildSiteV2Mutation,
  useGetSiteV2GetDataQuery,
  useGetSiteV2DeploysQuery,
  useBuildSiteCompilerV2Mutation,
  useLazyGetComponentUsageQuery,
} = mediacoreApi

// Configure RTK Query following official docs, see https://redux-toolkit.js.org/rtk-query/usage-with-typescript

// TODO: Split file for maintanability purposes.
